/**
 * API Client
 *
 * Handles all communication with the Zemer API.
 * Supports both HTTP (web/PWA) and Electron IPC (desktop).
 */

import { useAuthStore } from '@/stores/auth';
// Types from @/types/electron.d.ts are loaded via tsconfig

const API_BASE = import.meta.env.VITE_API_URL || '/api';

// Check if running in Electron (must be a function to check at runtime, not module load time)
const isElectron = (): boolean => {
  const result = typeof window !== 'undefined' && !!window.isElectron && !!window.zemerAPI;
  console.log('[API] isElectron check:', result, 'window.isElectron:', window?.isElectron, 'window.zemerAPI:', !!window?.zemerAPI);
  return result;
};

interface ApiResponse<T> {
  success: boolean;
  data?: T;
  error?: {
    code: string;
    message: string;
  };
}

class ApiClient {
  private baseUrl: string;

  constructor(baseUrl: string) {
    this.baseUrl = baseUrl;
  }

  private getHeaders(): Headers {
    const headers = new Headers({
      'Content-Type': 'application/json',
    });

    // Add auth token if available
    const { sessionToken } = useAuthStore.getState();
    if (sessionToken) {
      headers.set('Authorization', `Bearer ${sessionToken}`);
    }

    return headers;
  }

  /**
   * HTTP GET request (used in web/PWA mode)
   */
  private async httpGet<T>(endpoint: string): Promise<ApiResponse<T>> {
    const response = await fetch(`${this.baseUrl}${endpoint}`, {
      method: 'GET',
      headers: this.getHeaders(),
    });

    return response.json();
  }

  /**
   * HTTP POST request (used in web/PWA mode)
   */
  private async httpPost<T>(endpoint: string, body?: unknown): Promise<ApiResponse<T>> {
    const response = await fetch(`${this.baseUrl}${endpoint}`, {
      method: 'POST',
      headers: this.getHeaders(),
      body: body ? JSON.stringify(body) : undefined,
    });

    return response.json();
  }

  /**
   * Generic GET - uses IPC in Electron, HTTP otherwise
   */
  async get<T>(endpoint: string): Promise<ApiResponse<T>> {
    console.log('[API] GET', endpoint);
    // In Electron, route through IPC based on endpoint
    if (isElectron() && window.zemerAPI) {
      console.log('[API] Using Electron IPC for GET', endpoint);
      const result = await this.electronGet<T>(endpoint);
      console.log('[API] IPC result:', result);
      return result;
    }
    console.log('[API] Using HTTP for GET', endpoint);
    return this.httpGet<T>(endpoint);
  }

  /**
   * Generic POST - uses IPC in Electron, HTTP otherwise
   */
  async post<T>(endpoint: string, body?: unknown): Promise<ApiResponse<T>> {
    // In Electron, route through IPC based on endpoint
    if (isElectron() && window.zemerAPI) {
      return this.electronPost<T>(endpoint, body);
    }
    return this.httpPost<T>(endpoint, body);
  }

  /**
   * Route GET requests to appropriate IPC handler in Electron
   */
  private async electronGet<T>(endpoint: string): Promise<ApiResponse<T>> {
    const api = window.zemerAPI!.api;

    // Route based on endpoint pattern
    if (endpoint === '/whitelist') {
      return api.getWhitelist();
    }
    if (endpoint.startsWith('/browse/home')) {
      const refresh = endpoint.includes('refresh=1');
      return api.getHome(refresh);
    }
    if (endpoint.startsWith('/browse/artist/')) {
      const artistId = endpoint.replace('/browse/artist/', '');
      return api.getArtist(artistId);
    }
    if (endpoint.startsWith('/browse/album/')) {
      const albumId = endpoint.replace('/browse/album/', '');
      return api.getAlbum(albumId);
    }
    if (endpoint.startsWith('/browse/playlist/')) {
      const playlistId = endpoint.replace('/browse/playlist/', '');
      return api.getPlaylist(playlistId);
    }
    if (endpoint.startsWith('/player/')) {
      const videoId = endpoint.replace('/player/', '');
      return api.getPlayer(videoId);
    }
    if (endpoint.startsWith('/artist-thumbnail/')) {
      const artistId = endpoint.replace('/artist-thumbnail/', '');
      return api.getArtistThumbnail(artistId);
    }
    if (endpoint === '/auth/status') {
      return api.authStatus();
    }

    // Fallback to HTTP for unhandled endpoints
    console.warn(`[API] Unhandled Electron IPC endpoint: ${endpoint}, falling back to HTTP`);
    return this.httpGet<T>(endpoint);
  }

  /**
   * Route POST requests to appropriate IPC handler in Electron
   */
  private async electronPost<T>(endpoint: string, body?: unknown): Promise<ApiResponse<T>> {
    const api = window.zemerAPI!.api;

    if (endpoint === '/search') {
      const { query, filter } = body as { query: string; filter?: string };
      return api.search(query, filter);
    }
    if (endpoint === '/auth/login') {
      return api.authLogin();
    }
    if (endpoint === '/auth/logout') {
      return api.authLogout();
    }
    if (endpoint === '/auth/import') {
      const { cookies, visitorData } = body as { cookies: string; visitorData?: string };
      return api.authImport(cookies, visitorData);
    }

    // Fallback to HTTP for unhandled endpoints
    console.warn(`[API] Unhandled Electron IPC endpoint: ${endpoint}, falling back to HTTP`);
    return this.httpPost<T>(endpoint, body);
  }

  // Convenience methods for specific endpoints
  async search(query: string, filter?: string) {
    return this.post<{
      songs: unknown[];
      albums: unknown[];
      artists: unknown[];
      playlists: unknown[];
    }>('/search', { query, filter });
  }

  async getHome(options?: { refresh?: boolean }) {
    const query = options?.refresh ? '?refresh=1' : '';
    return this.get<{ sections: unknown[] }>(`/browse/home${query}`);
  }

  async getAlbum(id: string) {
    return this.get<{ album: unknown; songs: unknown[] }>(`/browse/album/${id}`);
  }

  async getArtist(id: string) {
    return this.get<{ artist: unknown; songs: unknown[]; albums: unknown[] }>(
      `/browse/artist/${id}`
    );
  }

  async getPlaylist(id: string) {
    return this.get<{ playlist: unknown; songs: unknown[] }>(`/browse/playlist/${id}`);
  }

  async getStreamUrl(videoId: string) {
    return this.get<{ streamUrl: string; expiresAt: number }>(`/player/${videoId}`);
  }

  async getArtistThumbnail(artistId: string) {
    return this.get<{ thumbnail: string | null }>(`/artist-thumbnail/${artistId}`);
  }

  // Auth methods
  async authStatus() {
    return this.get<{ authenticated: boolean }>('/auth/status');
  }

  async authLogin() {
    return this.post<{ authenticated: boolean }>('/auth/login');
  }

  async authLogout() {
    return this.post<{ success: boolean }>('/auth/logout');
  }
}

export const api = new ApiClient(API_BASE);

// Export utility to check if we're in Electron
export const isElectronApp = isElectron;
